/**
 * Pure mapping helper: profile row -> Braze user attributes.
 *
 * Kept side-effect-light and dependency-injectable so the mapping logic
 * (name parsing, phone gating, null-skipping, date coercion) is unit-testable
 * without the Braze SDK or a rendered AuthProvider. The default `setters` bind
 * to the real Braze wrappers; tests pass mock setters.
 *
 * Reserved Braze fields (first_name, last_name, phone, country) use the native
 * setters for out-of-the-box Liquid personalization; everything else is a
 * custom attribute. Attributes are skipped when the source value is null /
 * undefined / empty so we never overwrite a Braze value with a blank.
 *
 * SAFETY: this only WRITES per-user attributes. It fires no Braze events and
 * triggers no campaign/Canvas send. See .cursor/rules/braze-safety.mdc.
 */

import {
  brazeSetAttribute,
  brazeSetFirstName,
  brazeSetLastName,
  brazeSetPhoneNumber,
  brazeSetCountry,
} from './braze';

/** Profile columns consumed by the Braze attribute sync (REK-1403). */
export interface BrazeSyncProfile {
  full_name?: string | null;
  country?: string | null;
  phone_number?: string | null;
  phone_verified?: boolean | null;
  created_at?: string | null;
  signup_source?: string | null;
  signup_medium?: string | null;
  referred_by?: string | null;
  referral_code?: string | null;
  lifetime_points?: number | null;
  streak_count?: number | null;
  s4r_eligible?: boolean | null;
  last_login_at?: string | null;
}

/** Injectable setter surface — defaults to the real Braze wrappers. */
export interface BrazeProfileSetters {
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  setPhoneNumber: (v: string) => void;
  setCountry: (v: string) => void;
  setAttribute: (key: string, value: string | number | boolean | Date | null) => void;
}

const defaultSetters: BrazeProfileSetters = {
  setFirstName: brazeSetFirstName,
  setLastName: brazeSetLastName,
  setPhoneNumber: brazeSetPhoneNumber,
  setCountry: brazeSetCountry,
  setAttribute: brazeSetAttribute,
};

/**
 * Split a full name into first/last on the first whitespace run. The remainder
 * (everything after the first token) is the last name, so "Mary Jane Watson"
 * -> { firstName: "Mary", lastName: "Jane Watson" }. Returns empty strings for
 * blank / null input.
 */
export function parseFullName(fullName?: string | null): { firstName: string; lastName: string } {
  const trimmed = (fullName ?? '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? '';
  const lastName = parts.slice(1).join(' ');
  return { firstName, lastName };
}

/** Coerce an ISO timestamp string to a Date, or null when invalid/absent. */
function toValidDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Sync the profile-derived attributes for REK-1403 to the current Braze user.
 *
 * Native fields: first_name, last_name (parsed from full_name), country, and
 * phone_number (ONLY when phone_verified === true). Custom attributes:
 * phone_verified, signup_date, signup_source, signup_medium, referred_by,
 * referral_code, lifetime_points, streak_count, s4r_eligible, last_login_at.
 *
 * All values are skipped when null/undefined/empty so an absent column never
 * clobbers an existing Braze value.
 */
export function syncProfileAttributesToBraze(
  profile: BrazeSyncProfile,
  setters: BrazeProfileSetters = defaultSetters,
): void {
  if (!profile) return;

  // Native reserved fields.
  const { firstName, lastName } = parseFullName(profile.full_name);
  if (firstName) setters.setFirstName(firstName);
  if (lastName) setters.setLastName(lastName);

  if (profile.country) setters.setCountry(profile.country);

  // Phone: native field, gated on verification (issue P0 + braze-safety).
  if (profile.phone_verified === true && profile.phone_number) {
    setters.setPhoneNumber(profile.phone_number);
  }
  if (profile.phone_verified != null) {
    setters.setAttribute('phone_verified', profile.phone_verified);
  }

  // Custom attributes.
  const signupDate = toValidDate(profile.created_at);
  if (signupDate) setters.setAttribute('signup_date', signupDate);

  if (profile.signup_source) setters.setAttribute('signup_source', profile.signup_source);
  if (profile.signup_medium) setters.setAttribute('signup_medium', profile.signup_medium);
  if (profile.referred_by) setters.setAttribute('referred_by', profile.referred_by);
  if (profile.referral_code) setters.setAttribute('referral_code', profile.referral_code);

  if (profile.lifetime_points != null) {
    setters.setAttribute('lifetime_points', profile.lifetime_points);
  }
  if (profile.streak_count != null) {
    setters.setAttribute('streak_count', profile.streak_count);
  }
  if (profile.s4r_eligible != null) {
    setters.setAttribute('s4r_eligible', profile.s4r_eligible);
  }

  // REK-2114: replaces the `last_activity_at` attribute, which was written
  // from a column that never had a writer (it held the signup timestamp for
  // 32,152 of 32,166 production profiles). `last_login_at` is stamped on every
  // authenticated arrival by `stampLastLogin`, so it is genuinely recency.
  // NULL means "not observed since REK-2114 shipped" — skipped, like every
  // other empty value, so it never clobbers a Braze value with a blank.
  const lastLogin = toValidDate(profile.last_login_at);
  if (lastLogin) setters.setAttribute('last_login_at', lastLogin);
}
